import React, { useCallback, useEffect, useRef } from 'react';
import { Form } from 'antd';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import type {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js';
import { useIntl } from 'react-intl';

import { ErrorMessage } from '../error-message';
import { FormField } from '../form-field';

import type { StripeCardElementsGroup } from './types';

import styles from './styles.module.scss';

const ELEMENT_STYLE = {
  base: {
    fontSize: '15px',
    color: '#292929',
    '::placeholder': { color: '#bfbfbf' },
  },
  invalid: {
    color: '#ff4d4f',
  },
};

export interface StripeCardFieldsProps {
  isLoading?: boolean;
  /** Use `'2'` for a second card group (form fields cardNumber2, …). */
  fieldSuffix?: '' | '2';
  onElementsReady?: (group: StripeCardElementsGroup | null) => void;
}

export function StripeCardFields({
  isLoading = false,
  fieldSuffix = '',
  onElementsReady,
}: StripeCardFieldsProps): React.ReactElement {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const suffix = fieldSuffix === '2' ? '2' : '';
  const cardNumberField = suffix ? 'cardNumber2' : 'cardNumber';
  const cardExpiryField = suffix ? 'cardExpiry2' : 'cardExpiry';
  const cardCvcField = suffix ? 'cardCvc2' : 'cardCvc';

  const cardNumberError = Form.useWatch(cardNumberField, form);
  const cardExpiryError = Form.useWatch(cardExpiryField, form);
  const cardCvcError = Form.useWatch(cardCvcField, form);

  const partsRef = useRef<Partial<StripeCardElementsGroup>>({});
  const onElementsReadyRef = useRef(onElementsReady);
  onElementsReadyRef.current = onElementsReady;

  const publishIfComplete = useCallback(() => {
    const { cardNumber, cardExpiry, cardCvc } = partsRef.current;
    if (cardNumber && cardExpiry && cardCvc) {
      onElementsReadyRef.current?.({ cardNumber, cardExpiry, cardCvc });
    }
  }, []);

  const handleNumberReady = useCallback(
    (element: StripeCardNumberElement) => {
      partsRef.current.cardNumber = element;
      publishIfComplete();
    },
    [publishIfComplete],
  );

  const handleExpiryReady = useCallback(
    (element: StripeCardExpiryElement) => {
      partsRef.current.cardExpiry = element;
      publishIfComplete();
    },
    [publishIfComplete],
  );

  const handleCvcReady = useCallback(
    (element: StripeCardCvcElement) => {
      partsRef.current.cardCvc = element;
      publishIfComplete();
    },
    [publishIfComplete],
  );

  useEffect(() => {
    return () => {
      partsRef.current = {};
      onElementsReadyRef.current?.(null);
    };
  }, []);

  const syncFormField = useCallback(
    (field: string) => (e: { error?: { message: string } }) => {
      form?.setFieldValue(field, e.error?.message ?? '');
    },
    [form],
  );

  const cardNumberLabel = intl.formatMessage({ id: 'certificates.payment.stripe.cardLabel' });
  const cardNumberPlaceholder = intl.formatMessage({
    id: 'certificates.payment.stripe.cardPlaceholder',
  });
  const expiryLabel = intl.formatMessage({ id: 'certificates.payment.stripe.expiryLabel' });
  const expiryPlaceholder = intl.formatMessage({
    id: 'certificates.payment.stripe.expiryPlaceholder',
  });
  const cvcLabel = intl.formatMessage({ id: 'certificates.payment.stripe.cvcLabel' });
  const cvcPlaceholder = intl.formatMessage({ id: 'certificates.payment.stripe.cvcPlaceholder' });

  return (
    <div className={styles.stripeCardFields}>
      <div className={styles.formFieldWrapper}>
        <FormField
          name={cardNumberField}
          label={cardNumberLabel}
          variant="input"
          className={`${styles.formField} ${
            cardNumberError ? styles.formFieldWithErrorMessage : ''
          }`.trim()}
          validateStatus={cardNumberError ? 'error' : undefined}
        >
          <div className={styles.elementWrap}>
            <CardNumberElement
              options={{
                style: ELEMENT_STYLE,
                placeholder: cardNumberPlaceholder,
                disabled: isLoading,
              }}
              onChange={syncFormField(cardNumberField)}
              onReady={handleNumberReady}
            />
          </div>
        </FormField>
        <div
          className={`${styles.errorMessageWrapper} ${
            cardNumberError ? styles.errorMessageWrapperVisible : ''
          }`.trim()}
          aria-hidden={!cardNumberError}
        >
          {cardNumberError && (
            <ErrorMessage message={cardNumberError} className={styles.errorMessage} />
          )}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.formFieldWrapper}>
          <FormField
            name={cardExpiryField}
            label={expiryLabel}
            variant="input"
            className={`${styles.formField} ${
              cardExpiryError ? styles.formFieldWithErrorMessage : ''
            }`.trim()}
            validateStatus={cardExpiryError ? 'error' : undefined}
          >
            <div className={styles.elementWrap}>
              <CardExpiryElement
                options={{
                  style: ELEMENT_STYLE,
                  placeholder: expiryPlaceholder,
                  disabled: isLoading,
                }}
                onChange={syncFormField(cardExpiryField)}
                onReady={handleExpiryReady}
              />
            </div>
          </FormField>
          <div
            className={`${styles.errorMessageWrapper} ${
              cardExpiryError ? styles.errorMessageWrapperVisible : ''
            }`.trim()}
            aria-hidden={!cardExpiryError}
          >
            {cardExpiryError && (
              <ErrorMessage message={cardExpiryError} className={styles.errorMessage} />
            )}
          </div>
        </div>
        <div className={styles.formFieldWrapper}>
          <FormField
            name={cardCvcField}
            label={cvcLabel}
            variant="input"
            className={`${styles.formField} ${
              cardCvcError ? styles.formFieldWithErrorMessage : ''
            }`.trim()}
            validateStatus={cardCvcError ? 'error' : undefined}
          >
            <div className={styles.elementWrap}>
              <CardCvcElement
                options={{
                  style: ELEMENT_STYLE,
                  placeholder: cvcPlaceholder,
                  disabled: isLoading,
                }}
                onChange={syncFormField(cardCvcField)}
                onReady={handleCvcReady}
              />
            </div>
          </FormField>
          <div
            className={`${styles.errorMessageWrapper} ${
              cardCvcError ? styles.errorMessageWrapperVisible : ''
            }`.trim()}
            aria-hidden={!cardCvcError}
          >
            {cardCvcError && (
              <ErrorMessage message={cardCvcError} className={styles.errorMessage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
